import React, { useState, useEffect, useCallback } from 'react';
import { cn } from '@/lib/utils';
import { 
  Bot, 
  Compass, 
  Store, 
  Sparkles, 
  Newspaper, 
  Calendar, 
  TrendingUp, 
  Lightbulb,
  Mail
} from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

interface Section {
  id: string;
  label: string;
  icon: React.ElementType;
}

const sections: Section[] = [
  { id: 'featured-robots', label: 'Featured Robots', icon: Bot },
  { id: 'explore', label: 'Explore', icon: Compass },
  { id: 'marketplace', label: 'Marketplace', icon: Store },
  { id: 'recommender', label: 'AI Recommender', icon: Sparkles },
  { id: 'news', label: 'News', icon: Newspaper },
  { id: 'events', label: 'Events', icon: Calendar },
  { id: 'trends', label: 'Market Trends', icon: TrendingUp },
  { id: 'intelligence', label: 'Intelligence', icon: Lightbulb },
  { id: 'newsletter', label: 'Newsletter', icon: Mail },
];

export function FloatingSectionNav() {
  const [activeSection, setActiveSection] = useState<string>('');
  const [isVisible, setIsVisible] = useState(false);

  const handleScroll = useCallback(() => {
    // Show nav after scrolling past hero
    const scrollY = window.scrollY;
    setIsVisible(scrollY > 400);

    // Find current section
    const sectionElements = sections
      .map(s => ({ id: s.id, el: document.getElementById(s.id) }))
      .filter(s => s.el !== null);

    const viewportMiddle = scrollY + window.innerHeight / 3;

    for (let i = sectionElements.length - 1; i >= 0; i--) {
      const { id, el } = sectionElements[i];
      if (el && el.offsetTop <= viewportMiddle) {
        setActiveSection(id);
        break;
      }
    }
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      const offset = 80; // Header height
      const y = element.getBoundingClientRect().top + window.scrollY - offset;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  };

  if (!isVisible) return null;

  return (
    <TooltipProvider delayDuration={100}>
      <nav 
        className={cn(
          "fixed right-4 top-1/2 -translate-y-1/2 z-40",
          "hidden lg:flex flex-col gap-1.5",
          "bg-background/80 backdrop-blur-md border border-border rounded-full p-2",
          "shadow-lg",
          "animate-fade-in"
        )}
        aria-label="Page sections"
      >
        {sections.map((section) => {
          const Icon = section.icon;
          const isActive = activeSection === section.id;
          
          return (
            <Tooltip key={section.id}>
              <TooltipTrigger asChild>
                <button
                  onClick={() => scrollToSection(section.id)}
                  className={cn(
                    "w-9 h-9 rounded-full flex items-center justify-center transition-all duration-200",
                    "hover:bg-primary/10",
                    isActive 
                      ? "bg-primary text-primary-foreground shadow-sm" 
                      : "text-muted-foreground hover:text-foreground"
                  )}
                  aria-label={`Go to ${section.label}`}
                  aria-current={isActive ? 'true' : undefined}
                >
                  <Icon className="h-4 w-4" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="left" className="font-medium">
                {section.label}
              </TooltipContent>
            </Tooltip>
          );
        })}
      </nav>
    </TooltipProvider>
  );
}